import React from 'react';
import { Edit, List, MoreVertical, Trash2 } from '../../../bravosIcons';
import type { TrainerExerciseListRow } from '../../../../../types/dataModel';
import type { LibraryExercisesApi } from '../../hooks/useLibraryExercises';

const exerciseHeaderCellClass = 'px-4 md:px-6 py-4 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-theme-off-white/60';
const exerciseBodyCellClass = 'px-4 md:px-6 py-3 text-sm font-normal text-slate-600 dark:text-theme-off-white/80';

export type ExercisesTableMobileProps = {
  ex: LibraryExercisesApi;
  exerciseLoadError: string | null;
  profileId: string | undefined;
  trainerDisplayName: string;
  openExerciseMenu: string | null;
  setOpenExerciseMenu: (id: string | null) => void;
  exerciseMenuRef: React.RefObject<HTMLDivElement>;
  onViewExercise: (row: TrainerExerciseListRow) => void;
  onEditExercise: (id: string) => void;
  onDeleteExercise: (id: string, name: string) => void;
};

export function ExercisesTableMobile({
  ex,
  exerciseLoadError,
  profileId,
  trainerDisplayName,
  openExerciseMenu,
  setOpenExerciseMenu,
  exerciseMenuRef,
  onViewExercise,
  onEditExercise,
  onDeleteExercise,
}: ExercisesTableMobileProps) {
  const trimmedSearch = ex.exerciseNameFilter.trim().toLowerCase();
  const mobileRows = ex.filteredExercisesByName;

  return (
    <div className="overflow-hidden rounded-[28px] bg-white">
      {exerciseLoadError && (
        <div className="border-b border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{exerciseLoadError}</div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] border-collapse">
          <thead>
            <tr className="bg-white">
              <th className={exerciseHeaderCellClass}>Name</th>
              <th className={exerciseHeaderCellClass}>Created By</th>
              <th className={exerciseHeaderCellClass}>Muscle Group</th>
              <th className={exerciseHeaderCellClass}>Equipment</th>
              <th className={`${exerciseHeaderCellClass} text-right`}><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {mobileRows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-16 text-center">
                  <List className="mx-auto mb-4 h-12 w-12 text-slate-300" />
                  <h3 className="mb-2 text-lg font-medium text-slate-800">
                    {trimmedSearch ? 'No exercises match your search' : 'No exercises yet'}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {trimmedSearch ? 'Try a different search term.' : 'Exercises from the database will appear here.'}
                  </p>
                </td>
              </tr>
            ) : (
              mobileRows.map((exercise, index) => {
                const isLastRow = index === mobileRows.length - 1;
                const canDelete = exercise.createdBy === profileId;
                const createdByLabel = exercise.createdBy === profileId ? trainerDisplayName : 'Bravos';
                return (
                  <tr key={exercise.id} className="cursor-pointer hover:bg-slate-50/80" onClick={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.closest('button') || target.closest('.relative')) return;
                    onViewExercise(exercise);
                  }}>
                    <td className={exerciseBodyCellClass}>{exercise.name}</td>
                    <td className={exerciseBodyCellClass}>{createdByLabel}</td>
                    <td className={exerciseBodyCellClass}>{exercise.muscleGroup ?? '—'}</td>
                    <td className={exerciseBodyCellClass}>{exercise.equipment ?? '—'}</td>
                    <td className="px-4 md:px-6 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="relative inline-block" ref={openExerciseMenu === exercise.id ? exerciseMenuRef : undefined}>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setOpenExerciseMenu(openExerciseMenu === exercise.id ? null : exercise.id); }} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-transparent text-slate-500 transition-colors hover:border-slate-200 hover:bg-slate-50 hover:text-slate-700">
                          <MoreVertical className="w-5 h-5" />
                        </button>
                        {openExerciseMenu === exercise.id && (
                          <>
                            <div className="fixed inset-0 z-[100]" onClick={() => setOpenExerciseMenu(null)} />
                            <div className={`absolute right-0 z-[101] w-48 rounded-2xl border border-slate-200 bg-white py-1 shadow-[0_16px_40px_rgba(15,23,42,0.12)] ${isLastRow ? 'bottom-full mb-2' : 'mt-2'}`}>
                              <button type="button" onClick={() => { setOpenExerciseMenu(null); void onEditExercise(exercise.id); }} className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50">
                                <Edit className="w-4 h-4" /> Edit
                              </button>
                              {canDelete && (
                                <button type="button" onClick={() => onDeleteExercise(exercise.id, exercise.name)} className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-600 transition-colors hover:bg-red-50">
                                  <Trash2 className="w-4 h-4" /> Delete
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
